/**
 * @fileoverview Deterministic ROCAP valuation resolution
 * @module jobs/summarization/lib/rocapValuation
 *
 * Purpose:
 * - Decide which valuation is the directors' ROCAP figure for an asset
 * - Recognise Form 507 / ROCAP annexure sources even when the Asset List
 *   agent tagged them as Director Estimate or Balance Sheet
 *
 * Why:
 * - `chosenRocapValuationIndex` is written by the Asset Recovery agent, which
 *   is manual-only. Without this helper, annexure-disclosed assets show
 *   "No ROCAP figure" until someone clicks Run estimate.
 * - Annexure Part A/B schedules *are* the ROCAP disclosure — they should
 *   populate the ROCAP column, waterfall, and detail panel immediately.
 *
 * ROCAP-equivalent forms: a **Statement of Affairs** (SoA) and the pre-2017
 * **Report as to Affairs** (RATA) serve the same director-declaration role as
 * the ROCAP (Report on Company Activities and Property / Form 507). All three
 * are treated identically here — a figure disclosed on any of them, or on
 * their asset-schedule annexures, is a ROCAP figure.
 */

export type RocapValuationLike = {
  sourceType: string;
  value: number;
  notes?: string;
  sourceDocument?: {
    name: string;
    section?: string;
    documentType?: string;
  };
};

/**
 * Recognises a ROCAP (or ROCAP-equivalent) disclosure from the source
 * filename, section, document type, or notes:
 * - explicit ROCAP / Form 507 mentions
 * - Statement of Affairs (SoA) and Report as to Affairs (RATA) — same
 *   director-declaration role as a ROCAP
 * - ASIC Form 507 annexure naming ("Annexure Part A4", "Annexure Part B13")
 */
const ROCAP_ANNEXURE_RE =
  /\b(?:rocap|form\s*507|statement\s+of\s+affairs|soa|report\s+as\s+to\s+affairs|rata|annexure\s+part\s+[ab]\d*)\b/i;

/** True when `sourceType` is the literal ROCAP tag (any casing). */
export function isExplicitRocapSourceType(sourceType: string): boolean {
  return sourceType.trim().toLowerCase() === "rocap";
}

/**
 * True when a valuation is itself a ROCAP disclosure — either tagged ROCAP
 * or sourced from a ROCAP annexure / Form 507 document.
 */
export function isRocapSourcedValuation(
  valuation: RocapValuationLike,
): boolean {
  if (isExplicitRocapSourceType(valuation.sourceType)) return true;
  const haystack = [
    valuation.sourceDocument?.name,
    valuation.sourceDocument?.section,
    valuation.sourceDocument?.documentType,
    valuation.notes,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" ");
  return ROCAP_ANNEXURE_RE.test(haystack);
}

/**
 * Pick the single ROCAP valuation index from the list.
 *
 * Prefers an explicit `sourceType: "ROCAP"` over an annexure-derived figure.
 * Within the preferred class, takes the highest value (same rule as the
 * Asset Recovery agent). Returns null when nothing is ROCAP-sourced.
 */
export function inferRocapValuationIndex(
  valuations: ReadonlyArray<RocapValuationLike>,
): number | null {
  const candidates = valuations.flatMap((valuation, index) =>
    isRocapSourcedValuation(valuation) ? [{ valuation, index }] : [],
  );
  if (candidates.length === 0) return null;

  const explicit = candidates.filter((c) =>
    isExplicitRocapSourceType(c.valuation.sourceType),
  );
  const pool = explicit.length > 0 ? explicit : candidates;
  const chosen = pool.reduce((best, current) =>
    current.valuation.value > best.valuation.value ? current : best,
  );
  return chosen.index;
}

/**
 * The index that should drive the ROCAP figure.
 *
 * Honours an already-chosen index (recovery agent or a persisted inference)
 * when it still points at a real valuation; otherwise infers from sources.
 */
export function resolveRocapValuationIndex(
  valuations: ReadonlyArray<RocapValuationLike>,
  chosenIndex: number | undefined,
): number | null {
  if (
    chosenIndex != null &&
    chosenIndex >= 0 &&
    chosenIndex < valuations.length
  ) {
    return chosenIndex;
  }
  return inferRocapValuationIndex(valuations);
}

// ---------------------------------------------------------------------------
// Loans — derive per-source balance figures (mirrors asset valuations)
// ---------------------------------------------------------------------------

/**
 * One per-source figure for a loan's outstanding balance. Same shape the Loans
 * panel renders and the loans agent populates (see schema
 * `loanBalanceEstimateValidator`).
 */
export type LoanBalanceEstimateLike = {
  sourceType: string;
  directorName?: string;
  sourceDocument?: { name: string; section?: string; documentType: string };
  value: number;
  asAtDate?: string;
  notes?: string;
};

/** The minimal loan shape needed to derive its ROCAP balance figure. */
export type LoanRocapEntryLike = {
  outstandingBalance: number;
  date?: string;
  notes?: string;
  balanceEstimates?: ReadonlyArray<LoanBalanceEstimateLike>;
  sourceDocuments: ReadonlyArray<{ fileName: string }>;
};

/**
 * The per-source balance figures to display for a loan.
 *
 * Why this exists: the loans agent should populate `balanceEstimates`, but a
 * loan is frequently backed only by a director's ROCAP / Statement of Affairs
 * annexure (e.g. "Annexure Part A7 - Amounts the Company owes") with the
 * declared figure carried on the scalar `outstandingBalance`. Rather than
 * depend on the model tagging that structurally, we DERIVE a ROCAP estimate
 * from the loan's own source documents / notes — exactly how the Assets panel
 * recognises an annexed director estimate as the ROCAP figure. This keeps the
 * ROCAP column and detail panel populated deterministically.
 *
 * - If an explicit ROCAP-sourced estimate already exists, the list is returned
 *   unchanged.
 * - Otherwise, when a source document (or the notes) identifies a ROCAP / SoA
 *   / RATA disclosure, a director-declared estimate is synthesised from the
 *   scalar `outstandingBalance`. Its non-explicit `sourceType` means the UI
 *   renders it as "Derived from ROCAP", matching the Assets panel.
 */
export function deriveLoanBalanceEstimates(
  entry: LoanRocapEntryLike,
): Array<LoanBalanceEstimateLike> {
  const explicit = [...(entry.balanceEstimates ?? [])];
  if (explicit.some((estimate) => isRocapSourcedValuation(estimate))) {
    return explicit;
  }

  const rocapDoc = entry.sourceDocuments.find((doc) =>
    isRocapSourcedValuation({
      sourceType: "",
      value: 0,
      sourceDocument: { name: doc.fileName, documentType: "" },
    }),
  );
  const notesAreRocap = entry.notes
    ? isRocapSourcedValuation({ sourceType: "", value: 0, notes: entry.notes })
    : false;

  if (!rocapDoc && !notesAreRocap) return explicit;

  const synthesised: LoanBalanceEstimateLike = {
    // Non-explicit sourceType → the UI shows "Derived from ROCAP" (the figure
    // is recognised as ROCAP via its source document / notes, not a literal
    // ROCAP tag). Mirrors the annexed "Director Estimate" on the Assets panel.
    sourceType: "director_estimate",
    value: entry.outstandingBalance,
    asAtDate: entry.date,
    sourceDocument: rocapDoc
      ? { name: rocapDoc.fileName, documentType: "director_disclosures" }
      : undefined,
    notes: "Derived from the director's ROCAP disclosure.",
  };
  return [...explicit, synthesised];
}
